import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  Search,
  Send,
  User,
  Check,
  CheckCheck,
  MoreVertical,
  Phone,
  Video,
  Smile,
  Paperclip,
  ArrowLeft,
  MessageSquare,
  Users
} from 'lucide-react';

const MessagesPage = () => {
  const { user, getAccessToken } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const messagesEndRef = useRef(null);
  const [showMobileChat, setShowMobileChat] = useState(false);

  const isAdmin = user?.role === 'admin';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch contacts and build conversation list
  const fetchContactsAndConversations = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      
      // Fetch contacts and messages in parallel for faster loading
      const [contactsResponse, messagesResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/contacts`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/all?page=1&page_size=100`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      ]);
      
      let contactsList = [];
      if (contactsResponse.ok) {
        const contactsData = await contactsResponse.json();
        contactsList = contactsData.contacts || [];
        setContacts(contactsList);
      }

      if (messagesResponse.ok) {
        const data = await messagesResponse.json();
        
        // Combine inbox and sent messages
        const allMessages = [
          ...(data.inbox?.messages || []),
          ...(data.sent?.messages || [])
        ];

        // Group by conversation partner
        const conversationMap = {};
        const unreadMap = {};
        
        allMessages.forEach(msg => {
          const partnerId = msg.direction === 'received' ? msg.sender_id : msg.recipient_id;
          const partnerName = msg.direction === 'received' ? msg.sender_name : msg.recipient_name;
          const partnerRole = msg.direction === 'received' ? msg.sender_role : msg.recipient_role;
          
          if (!conversationMap[partnerId]) {
            conversationMap[partnerId] = {
              id: partnerId,
              name: partnerName || 'Unknown',
              role: partnerRole || 'user',
              lastMessage: msg.content,
              lastMessageTime: msg.created_at,
              unreadCount: 0,
              hasMessages: true
            };
          }
          
          // Update last message if newer
          if (new Date(msg.created_at) > new Date(conversationMap[partnerId].lastMessageTime)) {
            conversationMap[partnerId].lastMessage = msg.content;
            conversationMap[partnerId].lastMessageTime = msg.created_at;
          }

          // Count unread
          if (msg.direction === 'received' && !msg.is_read) {
            conversationMap[partnerId].unreadCount++;
            unreadMap[partnerId] = (unreadMap[partnerId] || 0) + 1;
          }
        });

        // For non-admin users: merge contacts with conversations
        // This shows all associated users even if no messages yet
        if (!isAdmin) {
          contactsList.forEach(contact => {
            if (!conversationMap[contact.id]) {
              conversationMap[contact.id] = {
                id: contact.id,
                name: contact.name,
                role: contact.role,
                lastMessage: '',
                lastMessageTime: null,
                unreadCount: 0,
                hasMessages: false
              };
            }
          });
        }

        // Sort: conversations with messages first, then by time
        const convList = Object.values(conversationMap)
          .sort((a, b) => {
            // Conversations with messages come first
            if (a.hasMessages && !b.hasMessages) return -1;
            if (!a.hasMessages && b.hasMessages) return 1;
            // Then sort by last message time
            if (a.lastMessageTime && b.lastMessageTime) {
              return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
            }
            // Then alphabetically by name
            return (a.name || '').localeCompare(b.name || '');
          });
        
        setConversations(convList);
        setUnreadCounts(unreadMap);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, isAdmin]);

  useEffect(() => {
    fetchContactsAndConversations();
  }, [fetchContactsAndConversations]);

  // Fetch messages for selected contact
  const fetchMessagesForContact = useCallback(async (contactId) => {
    if (!contactId) return;
    
    try {
      setLoadingMessages(true);
      const token = getAccessToken();
      
      // Use the optimized /all endpoint (cached on backend)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/all?page=1&page_size=100`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Filter messages for this conversation
        const received = (data.inbox?.messages || [])
          .filter(m => m.sender_id === contactId);
        
        const sent = (data.sent?.messages || [])
          .filter(m => m.recipient_id === contactId);
        
        // Combine and sort by time
        const allMessages = [...received, ...sent]
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        
        setMessages(allMessages);

        // Mark unread messages as read (batch the requests)
        const unreadMessages = received.filter(m => !m.is_read);
        if (unreadMessages.length > 0) {
          // Fire all mark-as-read requests in parallel
          await Promise.all(
            unreadMessages.map(msg =>
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/messages/${msg.id}/read`,
                { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
              ).catch(e => console.error('Failed to mark as read:', e))
            )
          );
        }

        // Update unread count
        setUnreadCounts(prev => ({ ...prev, [contactId]: 0 }));
        setConversations(prev => prev.map(c => 
          c.id === contactId ? { ...c, unreadCount: 0 } : c
        ));
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (selectedContact) {
      fetchMessagesForContact(selectedContact.id);
    }
  }, [selectedContact, fetchMessagesForContact]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedContact) return;

    try {
      setSendingMessage(true);
      const token = getAccessToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: selectedContact.id,
            content: messageInput.trim()
          })
        }
      );

      if (response.ok) {
        // Add message locally for instant feedback
        const newMessage = {
          id: Date.now().toString(),
          content: messageInput.trim(),
          direction: 'sent',
          created_at: new Date().toISOString(),
          is_read: false
        };
        setMessages(prev => [...prev, newMessage]);
        setMessageInput('');
        
        // Update conversation list
        setConversations(prev => {
          const updated = prev.map(c => 
            c.id === selectedContact.id 
              ? { ...c, lastMessage: messageInput.trim(), lastMessageTime: new Date().toISOString(), hasMessages: true }
              : c
          );
          return updated.sort((a, b) => {
            if (a.hasMessages && !b.hasMessages) return -1;
            if (!a.hasMessages && b.hasMessages) return 1;
            if (a.lastMessageTime && b.lastMessageTime) {
              return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
            }
            return 0;
          });
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setShowMobileChat(true);
  };

  const handleBackToList = () => {
    setShowMobileChat(false);
    setSelectedContact(null);
  };

  const startNewChat = (contact) => {
    const existingConv = conversations.find(c => c.id === contact.id);
    if (existingConv) {
      handleSelectContact(existingConv);
    } else {
      handleSelectContact({
        id: contact.id,
        name: contact.name,
        role: contact.role,
        lastMessage: '',
        lastMessageTime: new Date().toISOString(),
        unreadCount: 0,
        hasMessages: false
      });
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Today
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 172800000) { // Yesterday
      return 'Yesterday';
    } else if (diff < 604800000) { // This week
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'from-purple-500 to-purple-600',
      tutor: 'from-blue-500 to-blue-600',
      parent: 'from-green-500 to-green-600',
      student: 'from-yellow-500 to-orange-500'
    };
    return colors[role] || 'from-gray-500 to-gray-600';
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  // For admin: filter conversations and contacts by search
  // For others: filter only the displayed list (which includes all contacts)
  const filteredConversations = conversations.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Only show additional contacts for admin when searching
  const filteredContacts = isAdmin ? contacts.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !conversations.find(conv => conv.id === c.id)
  ) : [];

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // Group conversations by role for non-admin users
  const groupedConversations = !isAdmin ? {
    withMessages: filteredConversations.filter(c => c.hasMessages),
    admins: filteredConversations.filter(c => !c.hasMessages && c.role === 'admin'),
    tutors: filteredConversations.filter(c => !c.hasMessages && c.role === 'tutor'),
    parents: filteredConversations.filter(c => !c.hasMessages && c.role === 'parent'),
    students: filteredConversations.filter(c => !c.hasMessages && c.role === 'student'),
  } : null;

  const renderContactItem = (conv, showRoleBadge = true) => (
    <div
      key={conv.id}
      onClick={() => handleSelectContact(conv)}
      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
        selectedContact?.id === conv.id ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''
      }`}
    >
      {/* Avatar */}
      <div className={`relative w-12 h-12 rounded-full bg-gradient-to-br ${getRoleColor(conv.role)} flex items-center justify-center text-white font-semibold shadow-lg`}>
        {getInitials(conv.name)}
        {/* Online indicator */}
        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" />
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-900 dark:text-white truncate">
            {conv.name}
          </span>
          {conv.lastMessageTime && (
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
              {formatTime(conv.lastMessageTime)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {conv.lastMessage || (showRoleBadge ? `Tap to start chatting` : '')}
          </p>
          {conv.unreadCount > 0 && (
            <span className="ml-2 bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {conv.unreadCount}
            </span>
          )}
        </div>
        {showRoleBadge && !conv.hasMessages && (
          <span className={`text-xs px-2 py-0.5 rounded capitalize inline-block mt-1 ${
            conv.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
            conv.role === 'tutor' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
            conv.role === 'parent' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
          }`}>
            {conv.role}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-140px)] flex bg-gray-100 dark:bg-gray-900 rounded-2xl overflow-hidden shadow-xl">
      {/* Conversations List - Left Panel */}
      <div className={`${showMobileChat ? 'hidden lg:flex' : 'flex'} w-full lg:w-96 flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700`}>
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-cyan-600 to-cyan-700">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-white">Messages</h1>
            {totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {totalUnread}
              </span>
            )}
          </div>
          {/* Search - Only show for Admin */}
          {isAdmin && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search or start new chat"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full bg-white/10 text-white placeholder-white/60 border-none focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          )}
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full" />
            </div>
          ) : isAdmin ? (
            // Admin View: Search-based list
            <>
              {/* Existing Conversations */}
              {filteredConversations.map((conv) => renderContactItem(conv, false))}

              {/* New Contacts (not in conversations) - for Admin search */}
              {searchTerm && filteredContacts.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                    Start new chat
                  </div>
                  {filteredContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => startNewChat(contact)}
                      className="flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getRoleColor(contact.role)} flex items-center justify-center text-white font-semibold shadow-lg`}>
                        {getInitials(contact.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-gray-900 dark:text-white truncate block">
                          {contact.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {contact.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty State */}
              {filteredConversations.length === 0 && !searchTerm && (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No conversations yet</p>
                  <p className="text-sm">Search for a contact to start chatting</p>
                </div>
              )}
            </>
          ) : (
            // Non-Admin View: Show all contacts grouped by role
            <>
              {/* Conversations with messages */}
              {groupedConversations.withMessages.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center gap-2">
                    <MessageSquare className="w-3 h-3" />
                    Recent Chats
                  </div>
                  {groupedConversations.withMessages.map((conv) => renderContactItem(conv, false))}
                </>
              )}

              {/* Admins */}
              {groupedConversations.admins.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/30 text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Admins
                  </div>
                  {groupedConversations.admins.map((conv) => renderContactItem(conv, false))}
                </>
              )}

              {/* Tutors */}
              {groupedConversations.tutors.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Tutors
                  </div>
                  {groupedConversations.tutors.map((conv) => renderContactItem(conv, false))}
                </>
              )}

              {/* Parents */}
              {groupedConversations.parents.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-green-50 dark:bg-green-900/30 text-xs font-semibold text-green-600 dark:text-green-400 uppercase flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Parents
                  </div>
                  {groupedConversations.parents.map((conv) => renderContactItem(conv, false))}
                </>
              )}

              {/* Students */}
              {groupedConversations.students.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-900/30 text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Students
                  </div>
                  {groupedConversations.students.map((conv) => renderContactItem(conv, false))}
                </>
              )}

              {/* Empty State */}
              {filteredConversations.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                  <Users className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No contacts available</p>
                  <p className="text-sm text-center px-4">Contacts appear based on your task associations</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Window - Right Panel */}
      <div className={`${showMobileChat ? 'flex' : 'hidden lg:flex'} flex-1 flex-col bg-[#e5ddd5] dark:bg-gray-900`}>
        {selectedContact ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white">
              {/* Back button for mobile */}
              <button
                onClick={handleBackToList}
                className="lg:hidden p-2 hover:bg-white/10 rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              {/* Contact Avatar */}
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getRoleColor(selectedContact.role)} flex items-center justify-center text-white font-semibold shadow-lg`}>
                {getInitials(selectedContact.name)}
              </div>
              
              {/* Contact Info */}
              <div className="flex-1">
                <h2 className="font-semibold">{selectedContact.name}</h2>
                <p className="text-xs text-white/70 capitalize">{selectedContact.role}</p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <Video className="w-5 h-5" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <Phone className="w-5 h-5" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div 
              className="flex-1 overflow-y-auto p-4 space-y-2"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <div className="w-24 h-24 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center mb-4">
                    <MessageSquare className="w-12 h-12 text-cyan-500" />
                  </div>
                  <p className="text-lg font-medium">No messages yet</p>
                  <p className="text-sm">Send a message to start the conversation</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, index) => {
                    const isSent = msg.direction === 'sent';
                    const showDate = index === 0 || 
                      new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
                    
                    return (
                      <React.Fragment key={msg.id}>
                        {/* Date Separator */}
                        {showDate && (
                          <div className="flex justify-center my-4">
                            <span className="bg-white/80 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-3 py-1 rounded-full shadow-sm">
                              {new Date(msg.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        )}
                        
                        {/* Message Bubble */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
                              isSent
                                ? 'bg-cyan-500 text-white rounded-br-sm'
                                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-sm'
                            }`}
                          >
                            {/* Message Content */}
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            
                            {/* Time & Status */}
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isSent ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                              <span className="text-[10px]">
                                {formatMessageTime(msg.created_at)}
                              </span>
                              {isSent && (
                                msg.is_read 
                                  ? <CheckCheck className="w-3 h-3 text-blue-300" />
                                  : <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-3 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                {/* Emoji Button */}
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <Smile className="w-6 h-6" />
                </button>
                
                {/* Attachment Button */}
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <Paperclip className="w-6 h-6" />
                </button>
                
                {/* Input */}
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message"
                  className="flex-1 px-4 py-3 bg-white dark:bg-gray-700 rounded-full border-none focus:outline-none focus:ring-2 focus:ring-cyan-500 text-gray-900 dark:text-white"
                />
                
                {/* Send Button */}
                <button
                  type="submit"
                  disabled={!messageInput.trim() || sendingMessage}
                  className={`p-3 rounded-full transition-all ${
                    messageInput.trim()
                      ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Send className={`w-5 h-5 ${sendingMessage ? 'animate-pulse' : ''}`} />
                </button>
              </div>
            </form>
          </>
        ) : (
          /* Empty State - No Chat Selected */
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800">
            <div className="w-64 h-64 mb-8">
              <svg viewBox="0 0 303 172" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full opacity-50">
                <path d="M151.5 0C68.2 0 0 54.5 0 122c0 27.5 11.5 52.5 30.5 72.5L15 172h136.5c83.3 0 151.5-54.5 151.5-122S234.8 0 151.5 0z" fill="#06B6D4" fillOpacity="0.1"/>
                <circle cx="90" cy="86" r="12" fill="#06B6D4"/>
                <circle cx="151" cy="86" r="12" fill="#06B6D4"/>
                <circle cx="212" cy="86" r="12" fill="#06B6D4"/>
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              FourQ Messages
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
              {isAdmin 
                ? 'Search for a contact to start chatting with tutors, parents, and students.'
                : 'Select a contact from the list to start chatting.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
